/** @jsxImportSource @emotion/react */

import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginContext } from "../../context/login-context";

// import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { IconButton, Chip, Box } from "@mui/material";
import { GridToolbarContainer, GridToolbarFilterButton } from "@mui/x-data-grid";

// import useFarms from "../../hooks/use-farms.js";
import useStrategicAccounts from "../../hooks/use-strategic-accounts.js";
// import useRebates from "../../hooks/use-rebates.js";

import {
	CustomButton,
	DataGrid,
	Icon,
	Submenu,
} from "../../components/index.js";
import ModalAccount from "./modals/modal-account.js";
import ModalAccountRemove from "./modals/modal-account-remove.js";
import {
	CUSTOMER_TYPE_CHIP_COLORS,
	CUSTOMER_TYPE_FILTER_OPTIONS,
	CUSTOMER_TYPE_LABELS,
	CUSTOMER_TYPE_NONE_FILTER_VALUE,
} from "../../components/constants.js";

import css from "./accounts.module.css";
import toolbarCss from "../../components/custom-toolbar/custom-toolbar.module.css";

dayjs.extend(utc);

const Accounts = (props) => {
	const { runProtected, state: userData } = useContext(LoginContext);
	const navigate = useNavigate();

	const { strategicAccounts, refetchStrategicAccounts } = useStrategicAccounts();

	const [rows, setRows] = useState([]);
	const [showAccountModal, setShowAccountModal] = useState(false);

	useEffect(() => {
		setRows(strategicAccounts);
	}, [strategicAccounts]);

	const handleAddAccount = (id) => {
		setShowAccountModal(true);
	};

	const modalClose = () => {
		setShowAccountModal(false);
		refetchStrategicAccounts();
	};

	const columns = [
		{
			field: "name",
			headerName: "Company",
			type: "text",
		},
		{
			field: "farm",
			headerName: "Farm(s)",
			type: "text",
			renderCell: (params) => {
				return (
					<div className={css.farmList}>
						{params?.row?.farms?.map((farm, index) => (
							<div key={farm.id}>
								{farm.name}
								{index < params?.row?.farms.length - 1
									? ", "
									: ""}
							</div>
						))}
					</div>
				);
			},
		},
		{
			field: "customer_type",
			headerName: "Customer Type",
			type: "singleSelect",
			valueOptions: CUSTOMER_TYPE_FILTER_OPTIONS,
			valueGetter: (_, row) => row?.customer_type || CUSTOMER_TYPE_NONE_FILTER_VALUE,
			valueFormatter: (value) =>
				!value || value === CUSTOMER_TYPE_NONE_FILTER_VALUE
					? "None"
					: CUSTOMER_TYPE_LABELS[value],
			renderCell: (params) => {
				const customerType = params?.row?.customer_type;
				if (!customerType) return null;

				const chipColors = CUSTOMER_TYPE_CHIP_COLORS[customerType];
				return (
					<Chip
						label={CUSTOMER_TYPE_LABELS[customerType]}
						size="small"
						sx={{
							backgroundColor: chipColors.bg,
							color: chipColors.color,
							fontWeight: 500,
						}}
					/>
				);
			},
		},
		{
			field: "rebateLevel",
			headerName: "Rebate Level",
			type: "text",
			renderCell: (params) => {
				return (
					<div className={css.farmList}>
						{params?.row?.farms?.map((farm, index) => (
							<div key={farm.id}>
								{farm.name}
								{index < params?.row?.farms.length - 1
									? ", "
									: ""}
							</div>
						))}
					</div>
				);
			},
		},
		{
			field: "action",
			headerName: "",
			sortable: false,
			filterable: false,
			align: "right",
			renderCell: (params) => {
				return (
					<>
						<IconButton
							onClick={() => {
								navigate("/account/");
							}}
						>
							<Icon icon="ChevronRight" />
						</IconButton>
					</>
				);
			},
		},
	];

	const modals = (
		<>
			<ModalAccount
				open={showAccountModal}
				onClose={modalClose}
				account={{account_type: "STRATEGIC_ACCOUNT"}}
			/>
		</>
	);

	const AccountsToolbar = () => (
		<GridToolbarContainer className={toolbarCss.toolbar}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<GridToolbarFilterButton />
				<h1>Accounts</h1>
			</Box>
			{userData?.isValmontUser && (
				<CustomButton
					label="+ Add new account"
					color="secondary"
					onClick={() => handleAddAccount()}
				/>
			)}
		</GridToolbarContainer>
	);

	const table = (
		<DataGrid
			rows={rows}
			columns={columns}
			slots={{ toolbar: AccountsToolbar }}
			initialState={{
				sorting: {
					sortModel: [{ field: "name", sort: "asc" }],
				},
			}}
			onRowClick={(params) => {
				navigate(`/account/${encodeURIComponent(params.row.name)}`);
			}}
			sx={{
				"& .MuiDataGrid-columnSeparator": {
					display: "none",
				},
			}}
		/>
	);

	return (
		<>
			{table}
			{modals}
		</>
	);
};

export default Accounts;
